import React from "react";

import { KeyIcon, PencilIcon, UserCheck, UserX } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { Profile } from "~/pages/admin/types";
import { useProfileStore } from "~/store/profileStore";

interface UserTableProps {
  profiles: Profile[];
  loading: boolean;
  onEdit: (profile: Profile) => void;
  onDeactivate: (profile: Profile) => void;
  onReactivate: (profile: Profile) => void;
  onChangePassword: (profile: Profile) => void;
  selectedProfiles: Set<string>;
  onToggleSelection: (id: string) => void;
  onToggleAll: (selected: boolean) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  profiles,
  loading,
  onEdit,
  onDeactivate,
  onReactivate,
  onChangePassword,
  selectedProfiles,
  onToggleSelection,
  onToggleAll,
}) => {
  const { id: currentUserId, role: currentUserRole } = useProfileStore();

  // Calculate if all selectable users are selected
  const selectableProfiles = profiles.filter(
    (profile) =>
      profile.id !== currentUserId &&
      (currentUserRole === "superadmin" || profile.role !== "superadmin")
  );

  const allSelected =
    selectableProfiles.length > 0 &&
    selectableProfiles.every((profile) => selectedProfiles.has(profile.id));

  // Check if profile can be selected
  const canSelectProfile = (profile: Profile) => {
    if (profile.id === currentUserId) return false;
    if (profile.role === "superadmin" && currentUserRole !== "superadmin")
      return false;
    return true;
  };

  const getTooltipMessage = (
    profile: Profile,
    action: "edit" | "password" | "deactivate" | "reactivate"
  ) => {
    const isOwnProfile = profile.id === currentUserId;
    const isSuperadminProfile =
      profile.role === "superadmin" && currentUserRole !== "superadmin";

    if (isOwnProfile) {
      switch (action) {
        case "edit":
          return "No puedes editar tu propia cuenta desde aquí";
        case "password":
          return "No puedes cambiar tu propia contraseña desde aquí";
        case "deactivate":
          return "No puedes desactivar tu propia cuenta";
        case "reactivate":
          return "No puedes reactivar tu propia cuenta";
      }
    }

    if (isSuperadminProfile) {
      switch (action) {
        case "edit":
          return "Solo un superadmin puede editar cuentas de superadmin";
        case "password":
          return "Solo un superadmin puede cambiar contraseñas de superadmin";
        case "deactivate":
          return "Solo un superadmin puede desactivar cuentas de superadmin";
        case "reactivate":
          return "Solo un superadmin puede reactivar cuentas de superadmin";
      }
    }

    switch (action) {
      case "edit":
        return `Editar información de ${profile.name} ${profile.lastname}`;
      case "password":
        return `Cambiar contraseña de ${profile.name} ${profile.lastname}`;
      case "deactivate":
        return `Desactivar cuenta de ${profile.name} ${profile.lastname}`;
      case "reactivate":
        return `Reactivar cuenta de ${profile.name} ${profile.lastname}`;
    }
  };

  return (
    <TooltipProvider>
      {loading ? (
        <TableSkeleton rows={8} columns={7} />
      ) : (
        <Table className="!rounded border">
          <TableCaption>Lista de usuarios del sistema</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={onToggleAll}
                  disabled={selectableProfiles.length === 0}
                />
              </TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Teléfono</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Fecha Creación</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {profiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">
                  No hay usuarios registrados
                </TableCell>
              </TableRow>
            ) : (
              profiles.map((profile, index) => (
                <TableRow
                  key={profile.id}
                  style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                  className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedProfiles.has(profile.id)}
                      onCheckedChange={() => onToggleSelection(profile.id)}
                      disabled={!canSelectProfile(profile)}
                    />
                  </TableCell>
                  <TableCell>
                    {profile.name} {profile.lastname}
                  </TableCell>
                  <TableCell>{profile.email}</TableCell>
                  <TableCell>{profile.phone || "—"}</TableCell>
                  <TableCell>
                    {profile.role === "seller" && "Vendedor"}
                    {profile.role === "logistica" && "Logística"}
                    {profile.role === "cobranza" && "Cobranza"}
                    {profile.role === "facturacion" && "Facturación"}
                    {profile.role === "risk" && "Riesgo"}
                    {profile.role === "transportista" && "Transportista"}
                    {profile.role === "production" && "Producción"}
                    {profile.role === "produccion-admin" && "Producción Admin"}
                    {profile.role === "superadmin" && "Super Admin"}
                    {profile.role === "admin" && "Admin"}
                    {profile.role === "bodega" && "Bodega"}
                    {profile.role === "asesor-fiscal" && "Asesor Fiscal"}
                  </TableCell>
                  <TableCell>
                    {new Date(profile.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onEdit(profile)}
                            className="h-8 w-8"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p>{getTooltipMessage(profile, "edit")}</p>
                        </TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onChangePassword(profile)}
                            disabled={!canSelectProfile(profile)}
                            className="h-8 w-8"
                          >
                            <KeyIcon className="h-4 w-4 text-blue-500" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p>{getTooltipMessage(profile, "password")}</p>
                        </TooltipContent>
                      </Tooltip>

                      {profile.is_active ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onDeactivate(profile)}
                              disabled={!canSelectProfile(profile)}
                              className="h-8 w-8"
                            >
                              <UserX className="h-4 w-4 text-orange-500" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p>{getTooltipMessage(profile, "deactivate")}</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onReactivate(profile)}
                              disabled={!canSelectProfile(profile)}
                              className="h-8 w-8"
                            >
                              <UserCheck className="h-4 w-4 text-green-500" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p>{getTooltipMessage(profile, "reactivate")}</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </TooltipProvider>
  );
};
